/**
 * <media-gallery>: wires an Embla Carousel to the gallery's arrows, dots, counter and thumbnails.
 * Expects `EmblaCarousel` (assets/embla-carousel.umd.js) to be loaded first.
 */
class MediaGallery extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  embla;

  #abort = new AbortController();

  connectedCallback() {
    const viewport = this.querySelector('[data-viewport]');
    if (!viewport || this.embla) return;

    const slides = this.querySelectorAll('[data-slide]');
    const multiple = slides.length > 1;

    this.embla = window.EmblaCarousel(viewport, {
      loop: multiple,
      watchDrag: multiple,
      align: 'start',
      duration: 22,
    });

    const { signal } = this.#abort;
    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('variant:change', this.#handleVariantChange, { signal });
    this.embla.on('select', this.#update).on('reInit', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.embla?.destroy();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element) || !this.embla) return;

    const control = event.target.closest('[data-action], [data-dot], [data-thumb]');
    if (!control || !this.contains(control)) return;

    if (control instanceof HTMLElement) {
      if (control.dataset.action === 'prev') this.embla.scrollPrev();
      else if (control.dataset.action === 'next') this.embla.scrollNext();
      else if (control.dataset.dot !== undefined) this.embla.scrollTo(Number(control.dataset.dot));
      else if (control.dataset.thumb !== undefined) this.embla.scrollTo(Number(control.dataset.thumb));
    }
  };

  /**
   * Scrolls to the chosen variant's featured media.
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    if (!(event instanceof CustomEvent) || !(event.target instanceof Element) || !this.embla) return;

    // Ignore pickers for other products on the page
    const picker = event.target.closest('variant-picker');
    if (picker instanceof HTMLElement && picker.dataset.productId !== this.dataset.productId) return;

    const mediaId = event.detail.variant.featured_media_id;
    if (!mediaId) return;

    const index = this.embla.slideNodes().findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index >= 0) this.embla.scrollTo(index);
  };

  #update = () => {
    if (!this.embla) return;

    const index = this.embla.selectedScrollSnap();

    for (const control of this.querySelectorAll('[data-dot], [data-thumb]')) {
      if (!(control instanceof HTMLElement)) continue;

      const controlIndex = Number(control.dataset.dot ?? control.dataset.thumb);
      const isCurrent = controlIndex === index;
      control.setAttribute('aria-current', String(isCurrent));

      if (isCurrent && control.dataset.thumb !== undefined) this.#revealThumbnail(control);
    }

    for (const counter of this.querySelectorAll('[data-counter-current]')) counter.textContent = String(index + 1);

    // Stop videos that have been swiped out of view
    this.embla.slideNodes().forEach((slide, i) => {
      if (i === index) return;
      for (const video of slide.querySelectorAll('video')) video.pause();
      for (const iframe of slide.querySelectorAll('iframe[src*="youtube"]')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      }
    });
  };

  /**
   * Scrolls the thumbnail rail (not the page) so the current thumbnail is visible.
   * @param {HTMLElement} thumb
   */
  #revealThumbnail(thumb) {
    const rail = thumb.parentElement;
    if (!rail) return;

    rail.scrollTo({
      left: thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2,
      top: thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
